import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { Filter, WidgetType } from './search_filters';
import MarketplaceFilterRow from './marketplace_filter_row';
import { RCPopover } from '@reverbdotcom/cadence/components';
import SelectFacet from './select_facet';
import FilterChip from './filter_chip';
import { I18N } from './translate';
import { Location } from 'history';
import ListViewToggle from './list_view_toggle';

interface IProps {
  filters: Filter[];
  followButton?: React.ReactNode;
  tipText?: string;
  totalText?: string;
  titleOverride?: string;
  loading?: boolean;
  resultsTotal?: number;
  trackingQuery?: string;
  location: Location;
  setListViewState?: (val: boolean) => void;
  showListViewToggle?: boolean;
}

// Rewrite of existing Overview component for use in marketplace_filter_row experiment
export default function RQLSearchOverview({
  filters = [],
  followButton = null,
  totalText = '',
  tipText = '',
  loading = false,
  resultsTotal = null,
  trackingQuery = '',
  titleOverride = null,
  location = null,
  setListViewState,
  showListViewToggle = false,
}: IProps) {
  const sort = filters.find(filter => filter.widgetType === WidgetType.SORT);
  const classes = classNames(
    'search-overview',
    'search-overview--new',
    { 'search-overview--loading': loading },
  );

  return (
    <nav className={classes}>
      <section className="search-overview__primary">
        <div className="search-overview__count">
          {loading && filters.length === 0 &&
            <>
              <div className="search-overview__loading-text" />
              <div className="size-70 mt-half">
                <div className="search-overview__loading-text" />
              </div>
            </>
          }
          {!(loading && filters.length === 0) &&
            <>
              <h2>
                <Title
                  filters={filters}
                  resultsTotal={resultsTotal}
                  totalText={totalText}
                  titleOverride={titleOverride}
                />
              </h2>
              <div className="size-70 mt-space d-flex fx-align-center">
                {tipText}
                {followButton}
              </div>
            </>
          }
        </div>
        {showListViewToggle &&
          <SearchOverviewListViewToggle
            loading={loading}
            setListViewState={setListViewState}
            filters={filters}
          />
        }
        <div className="search-overview__feed">
          <SortChip
            loading={loading}
            sort={sort}
            trackingQuery={trackingQuery}
          />
        </div>
      </section>
      <div className="search-overview__secondary">
        <MarketplaceFilterRow
          filters={filters}
          loading={loading}
          trackingQuery={trackingQuery}
          displayCount
          location={location}
          totalText={totalText}
          hideOnMobile
          dynamicTitle
          setListViewState={setListViewState}
          showListViewToggle={showListViewToggle}
        />
      </div>
    </nav>
  );
}

function Title({ filters, resultsTotal, totalText, titleOverride }) {
  const queryFilter = filters.filter(f => {
    return f.key === 'TEXT_QUERY';
  });

  const queryString = queryFilter && queryFilter.length > 0 ?
    queryFilter[0].options[0].optionValue :
    null;

  if (titleOverride) {
    return titleOverride;
  }

  if (queryString && resultsTotal) {
    return (
      <I18N
        text="commons.marketplaceFilters.resultsCountFor"
        args={{ count: I18n.toNumber(resultsTotal, { precision: 0 }) }}
      >
        <span className="weight-bold">
          &ldquo;{queryString}&rdquo;
        </span>
      </I18N>
    );
  }

  if (resultsTotal) {
    return (
      <I18N
        text="commons.marketplaceFilters.resultsCount"
        args={{ count: I18n.toNumber(resultsTotal, { precision: 0 }) }}
      />
    );
  }

  return totalText;
}

export function SortChip({ loading, sort, trackingQuery }) {
  const [isPopoverOpen, openPopover] = React.useState(false);

  if (loading && !sort) {
    return (
      <div className="mobile-d-none">
        <FilterChip
          key="sort"
          loading
        >
          <div className="filter-chip__loading-placeholder" />
        </FilterChip>
      </div>
    );
  }
  if (!sort) return null;

  const selected = sort?.options.find(opt => opt.selected);
  return (
    <div className="mobile-d-none">
      <RCPopover
        isOpen={isPopoverOpen}
        onDismiss={() => openPopover(false)}
        loading={loading}
        anchor={
          <FilterChip
            withDropdown
            loading={loading}
            onClick={() => openPopover(!isPopoverOpen)}
          >
            <span className="weight-bold">{`${sort.name}: `}</span>
            {selected?.name || ''}
          </FilterChip>
        }
      >
        <SelectFacet
          pageSection="search-overview"
          filter={sort}
          trackingQuery={trackingQuery}
        />
      </RCPopover>
    </div>
  );
}

function SearchOverviewListViewToggle({ loading, filters, setListViewState }) {
  return (
    <div className="search-overview__list-view-toggle">
      {(loading && filters.length === 0) ? (
        <div className="search-overview__list-view-toggle-loading-placeholder" />
      ) :
        (
          <ListViewToggle
            setListViewState={setListViewState}
            loading={loading}
          />
        )}
    </div>
  );
}
